import type { PropertyDescriptor, SourceContext, SourceStatus, SourceTypeDefinition } from '../core/definitions';
import { bool, num, str } from './base';
import { DeviceWatch, pickPairedAudio, type AcquiredDevice } from './deviceWatch';
import { mapMediaError } from './errors';
import { StreamRuntime, stopTracks, watchEnded } from './streamRuntime';

export type CameraSettings = {
  deviceId: string;
  width: number;
  height: number;
  frameRate: number;
  aspectRatio: 'auto' | '16:9' | '4:3' | '1:1' | '21:9';
  resizeMode: 'auto' | 'none' | 'crop-and-scale';
  includeAudio: boolean;
  /** '' uses the microphone built into the camera (same hardware group), when there is one. */
  audioDeviceId: string;
  /** Release the device while no visible item shows the source (camera light off); reopens when shown again. */
  deactivateWhenHidden: boolean;
};

const DEFAULTS: CameraSettings = {
  deviceId: '',
  width: 1280,
  height: 720,
  frameRate: 30,
  aspectRatio: 'auto',
  resizeMode: 'auto',
  includeAudio: false,
  audioDeviceId: '',
  deactivateWhenHidden: false,
};

/** Edits arriving within this window become one reconciliation, so a burst never reopens the device repeatedly. */
export const UPDATE_COALESCE_MS = 150;

const ASPECT_RATIOS: Record<string, number> = { '16:9': 16 / 9, '4:3': 4 / 3, '1:1': 1, '21:9': 21 / 9 };

function read(s: Partial<CameraSettings>): CameraSettings {
  const aspect = str(s.aspectRatio, DEFAULTS.aspectRatio);
  const resize = str(s.resizeMode, DEFAULTS.resizeMode);
  return {
    deviceId: str(s.deviceId, DEFAULTS.deviceId),
    width: num(s.width, DEFAULTS.width, 0, 7680),
    height: num(s.height, DEFAULTS.height, 0, 4320),
    frameRate: num(s.frameRate, DEFAULTS.frameRate, 0, 240),
    aspectRatio: aspect in ASPECT_RATIOS ? (aspect as CameraSettings['aspectRatio']) : 'auto',
    resizeMode: resize === 'none' || resize === 'crop-and-scale' ? resize : 'auto',
    includeAudio: bool(s.includeAudio, DEFAULTS.includeAudio),
    audioDeviceId: str(s.audioDeviceId, DEFAULTS.audioDeviceId),
    deactivateWhenHidden: bool(s.deactivateWhenHidden, DEFAULTS.deactivateWhenHidden),
  };
}

/** Constraints a running track can take through applyConstraints without reopening the device. */
export function softConstraints(c: CameraSettings): MediaTrackConstraints {
  const out: MediaTrackConstraints & { resizeMode?: ConstrainDOMString } = {};
  if (c.width > 0) out.width = { ideal: c.width };
  if (c.height > 0) out.height = { ideal: c.height };
  if (c.frameRate > 0) out.frameRate = { ideal: c.frameRate };
  if (c.aspectRatio !== 'auto') out.aspectRatio = { ideal: ASPECT_RATIOS[c.aspectRatio] };
  if (c.resizeMode !== 'auto') out.resizeMode = { ideal: c.resizeMode };
  return out;
}

export function cameraConstraints(c: CameraSettings): MediaTrackConstraints {
  const video: MediaTrackConstraints = softConstraints(c);
  if (c.deviceId) video.deviceId = { exact: c.deviceId };
  return video;
}

/** The paired microphone is a production input: browser voice processing stays off, as for the Audio Input source. */
export function pairedAudioConstraints(deviceId: string | null): MediaTrackConstraints {
  const audio: MediaTrackConstraints = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };
  if (deviceId) audio.deviceId = { exact: deviceId };
  return audio;
}

type Soft = Pick<CameraSettings, 'width' | 'height' | 'frameRate' | 'aspectRatio' | 'resizeMode'>;
const softChanged = (a: Soft, b: Soft): boolean =>
  a.width !== b.width || a.height !== b.height || a.frameRate !== b.frameRate || a.aspectRatio !== b.aspectRatio || a.resizeMode !== b.resizeMode;
const hardChanged = (a: CameraSettings, b: CameraSettings): boolean =>
  a.deviceId !== b.deviceId || a.includeAudio !== b.includeAudio || a.audioDeviceId !== b.audioDeviceId;

class CameraRuntime extends StreamRuntime<CameraSettings> {
  private cfg: CameraSettings;
  /** What the open device (or the attempt in flight) was configured with. */
  private applied: CameraSettings;
  private stream: MediaStream | null = null;
  private audioStream: MediaStream | null = null;
  private audioNote = '';
  private acquiredDevice: AcquiredDevice | null = null;
  private inFlight = false;
  private updateTimer: ReturnType<typeof setTimeout> | null = null;
  private showing = true;
  private acquisitions = 0;
  private releasedByHide = false;
  private unwatch: (() => void) | null = null;
  private readonly deviceWatch: DeviceWatch;

  constructor(ctx: SourceContext, settings: Partial<CameraSettings>) {
    super(ctx);
    this.cfg = read(settings);
    this.applied = this.cfg;
    this.deviceWatch = new DeviceWatch({
      kind: 'videoinput',
      configuredId: () => this.cfg.deviceId,
      acquired: () => this.acquiredDevice,
      status: () => {
        const s = this.getStatus();
        return { state: s.state, errorCode: s.error?.code };
      },
      reacquire: () => void this.acquire(),
    });
    // With "deactivate when hidden" the manager is about to say whether the item is showing; opening the
    // device first would flash the camera light for a source that starts hidden.
    if (this.cfg.deactivateWhenHidden) {
      queueMicrotask(() => {
        // setShowing() may already have opened (or deliberately not opened) the device.
        if (this.acquisitions === 0 && !this.destroyed && !this.releasedByHide) void this.acquire();
      });
    }
    else void this.acquire();
  }

  setShowing(showing: boolean): void {
    if (this.destroyed || showing === this.showing) return;
    this.showing = showing;
    this.applyVisibility();
  }

  private hidden(): boolean {
    return this.cfg.deactivateWhenHidden && !this.showing;
  }

  /** Releases the device when hidden and reopens it when shown again; a no-op while nothing changed. */
  private applyVisibility(): void {
    if (this.hidden()) {
      if (!this.releasedByHide) this.releaseForHide();
    } else if (this.releasedByHide) {
      this.releasedByHide = false;
      void this.acquire();
    }
  }

  private releaseForHide(): void {
    this.releasedByHide = true;
    this.beginEpoch(); // drops an acquisition still in flight; its stream is stopped when it lands
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.inFlight = false;
    this.releaseStream();
    this.acquiredDevice = null;
    this.setStatus('idle', undefined, 'paused');
  }

  update(settings: CameraSettings): void {
    if (this.destroyed) return;
    this.cfg = read(settings);
    this.applyVisibility();
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => {
      this.updateTimer = null;
      void this.reconcile();
    }, UPDATE_COALESCE_MS);
  }

  invoke(action: string): void {
    if (action === 'retry' && !this.destroyed) void this.acquire();
  }

  getReactionTrack(): MediaStreamTrack | null {
    return this.getStatus().state === 'live' ? this.stream?.getVideoTracks()[0] ?? null : null;
  }

  getCapabilities(): MediaTrackCapabilities | null {
    const track = this.stream?.getVideoTracks()[0];
    return track?.getCapabilities?.() ?? null;
  }

  getLiveInfo(): Record<string, string> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return {};
    const s = track.getSettings?.() ?? {};
    const info: Record<string, string> = {};
    if (track.label) info.device = track.label;
    if (s.width && s.height) info.resolution = `${s.width}×${s.height}`;
    if (s.frameRate) info.frameRate = `${Math.round(s.frameRate * 10) / 10} fps`;
    const mic = this.audioStream?.getAudioTracks()[0];
    if (this.cfg.includeAudio) info.audio = mic ? `${mic.label || 'microphone'} (processing off)` : this.audioNote || 'not available';
    return info;
  }

  /** Brings the open device in line with the latest settings with the least disruption. */
  private async reconcile(): Promise<void> {
    if (this.destroyed || this.inFlight) return; // the in-flight acquisition reconciles when it finishes
    const next = this.cfg;
    const live = this.stream?.getVideoTracks().some((t) => t.readyState === 'live') ?? false;
    if (hardChanged(this.applied, next)) {
      void this.acquire();
      return;
    }
    if (!softChanged(this.applied, next)) return;
    if (!live) {
      // Nothing is open (error or ended): size and rate alone do not justify a new attempt.
      this.applied = next;
      return;
    }
    const track = this.stream!.getVideoTracks()[0];
    try {
      await track.applyConstraints(softConstraints(next));
      this.applied = next;
      this.emit();
    } catch {
      // The device refused the in-place change; reopening is the fallback.
      void this.acquire();
    }
  }

  private releaseStream(): void {
    this.stopWatching();
    this.unwatch?.();
    this.unwatch = null;
    this.clearOutput();
    stopTracks(this.stream);
    stopTracks(this.audioStream);
    this.stream = null;
    this.audioStream = null;
  }

  private async acquireAudio(video: MediaStream): Promise<MediaStream | null> {
    const cfg = this.cfg;
    let id: string | null = cfg.audioDeviceId || null;
    this.audioNote = '';
    if (!id) {
      // Device ids and groups are only populated once the camera permission exists, hence after the video request.
      let devices: MediaDeviceInfo[] = [];
      try {
        devices = (await navigator.mediaDevices.enumerateDevices?.()) ?? [];
      } catch {
        devices = [];
      }
      const settings = video.getVideoTracks()[0]?.getSettings?.() ?? {};
      const groupId = settings.groupId || devices.find((d) => d.deviceId === settings.deviceId)?.groupId;
      id = pickPairedAudio(devices, groupId);
      if (!id) this.audioNote = 'No microphone is built into this camera; using the default microphone.';
    }
    return navigator.mediaDevices.getUserMedia({ audio: pairedAudioConstraints(id), video: false });
  }

  private async acquire(): Promise<void> {
    const epoch = this.beginEpoch();
    this.acquisitions++;
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    // Release first: many devices cannot be opened twice, so the old handle must go before the new request.
    this.releaseStream();
    this.acquiredDevice = null;
    if (this.hidden()) {
      this.releasedByHide = true;
      this.applied = this.cfg;
      this.setStatus('idle', undefined, 'paused');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      this.fail('unsupported', 'getUserMedia is unavailable (requires a secure context)');
      return;
    }
    this.inFlight = true;
    const snapshot = this.cfg;
    this.applied = snapshot;
    this.setStatus('starting');
    try {
      let video: MediaStream;
      try {
        video = await navigator.mediaDevices.getUserMedia({ video: cameraConstraints(snapshot), audio: false });
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        const mapped = mapMediaError(err);
        this.fail(mapped.code, mapped.message);
        return;
      }
      if (!this.isCurrent(epoch)) {
        stopTracks(video);
        return;
      }
      let audio: MediaStream | null = null;
      if (snapshot.includeAudio) {
        try {
          audio = await this.acquireAudio(video);
        } catch (err) {
          // The picture matters more than the sound: keep the camera and say why there is no audio.
          this.audioNote = `The microphone could not be opened: ${mapMediaError(err).message}`;
          this.log('warn', 'paired microphone failed', err);
        }
        if (!this.isCurrent(epoch)) {
          stopTracks(video);
          stopTracks(audio);
          return;
        }
      }
      this.stream = video;
      this.audioStream = audio;
      const videoTrack = video.getVideoTracks()[0];
      const s = videoTrack?.getSettings?.() ?? {};
      this.acquiredDevice = { deviceId: s.deviceId || snapshot.deviceId, groupId: s.groupId ?? '' };
      this.unwatch = watchEnded(video.getVideoTracks(), () => {
        if (this.isCurrent(epoch)) this.markEnded('waiting-for-device');
      });
      const outgoing = audio ? new MediaStream([...video.getTracks(), ...audio.getTracks()]) : video;
      try {
        if (!(await this.present(outgoing, epoch, { video: true, audio: audio !== null }))) return;
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.fail('unknown', err instanceof Error ? err.message : String(err));
        return;
      }
      if (video.getVideoTracks().every((t) => t.readyState === 'ended')) {
        this.markEnded('waiting-for-device');
        return;
      }
      this.setStatus('live');
      this.watchLiveness(video.getVideoTracks(), {
        code: 'in-use',
        message: 'The camera is not delivering frames. Another application may be using it, or it was disconnected.',
      });
    } finally {
      if (this.isCurrent(epoch)) {
        this.inFlight = false;
        // Edits made while the device was opening are applied now, against what is actually open.
        if (hardChanged(this.applied, this.cfg) || softChanged(this.applied, this.cfg)) void this.reconcile();
      }
    }
  }

  protected release(): void {
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.deviceWatch.dispose();
    this.releaseStream();
  }
}

export const cameraDefinition: SourceTypeDefinition<CameraSettings> = {
  kind: 'camera',
  displayName: 'Video Capture Device',
  caps: { video: true, audio: true, retry: true },
  category: 'capture',
  duplicate: 'reference',
  defaults: () => ({ ...DEFAULTS }),
  properties(settings: CameraSettings, status?: SourceStatus): PropertyDescriptor[] {
    const props: PropertyDescriptor[] = [
      { key: 'deviceId', label: 'Device', type: 'device', deviceKind: 'videoinput' },
      { key: 'width', label: 'Width', type: 'number', min: 0, max: 7680, step: 1, unit: 'px', applyOn: 'commit', description: '0 lets the browser choose.' },
      { key: 'height', label: 'Height', type: 'number', min: 0, max: 4320, step: 1, unit: 'px', applyOn: 'commit' },
      { key: 'frameRate', label: 'Frame rate', type: 'number', min: 0, max: 240, step: 1, unit: 'fps', applyOn: 'commit' },
      {
        key: 'aspectRatio',
        label: 'Aspect ratio',
        type: 'select',
        options: [
          { value: 'auto', label: 'Device default' },
          { value: '16:9', label: '16:9' },
          { value: '4:3', label: '4:3' },
          { value: '1:1', label: '1:1' },
          { value: '21:9', label: '21:9' },
        ],
      },
      {
        key: 'resizeMode',
        label: 'Resize mode',
        type: 'select',
        description: 'Whether the browser may crop and scale the capture to the requested size (Chromium).',
        options: [
          { value: 'auto', label: 'Browser default' },
          { value: 'none', label: 'Native sensor modes only' },
          { value: 'crop-and-scale', label: 'Crop and scale' },
        ],
      },
      { key: 'includeAudio', label: "Also capture the camera's microphone", type: 'boolean', description: 'Uses the microphone built into the camera with browser voice processing off.' },
      { key: 'audioDeviceId', label: 'Microphone', type: 'device', deviceKind: 'audioinput', pairWith: 'deviceId', visibleWhen: (s) => s.includeAudio === true },
      {
        key: 'deactivateWhenHidden',
        label: 'Release the camera while it is not showing',
        type: 'boolean',
        description: 'Turns the camera light off when no visible item uses this source. It opens again, which takes about a second, when shown.',
      },
      { key: 'liveDevice', label: 'Device in use', type: 'info', liveKey: 'device' },
      { key: 'liveResolution', label: 'Delivering', type: 'info', liveKey: 'resolution' },
      { key: 'liveFrameRate', label: 'Frame rate', type: 'info', liveKey: 'frameRate' },
    ];
    if (settings.includeAudio) props.push({ key: 'liveAudio', label: 'Microphone', type: 'info', liveKey: 'audio' });
    if (!status || status.state === 'error' || status.state === 'ended') {
      props.push({ key: 'retry', label: 'Reconnect', type: 'button', action: 'retry' });
    }
    return props;
  },
  create: (ctx, settings) => new CameraRuntime(ctx, settings),
};
